import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, ShieldCheck } from "lucide-react";
import { Toaster, toast } from "sonner";
import EmergencyAnnouncementBar from "@/components/EmergencyAnnouncementBar";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatLocalDateTime = (value) => {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
};

const errorMessage = (payload) => {
  if (typeof payload?.detail === "string") return payload.detail;
  if (Array.isArray(payload?.detail)) return payload.detail.map((item) => item.msg).join(" ");
  return "We could not record your check-in. Please try again.";
};

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

const CheckinPage = () => {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [emailRegistered, setEmailRegistered] = useState(null);
  const [guestPrompt, setGuestPrompt] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", attendee_count: "1", phone: "" });
  const [dynamicAnswers, setDynamicAnswers] = useState({});

  const dynamicQuestions = useMemo(() => session?.dynamic_questions || [], [session]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    document.title = "Session Check-In | MyEnglish Lesson.ca";
    description?.setAttribute("content", "Official student check-in portal for MyEnglish Lesson.ca. Check in within the first 20 minutes to verify attendance and claim your Class Pass reward.");
    return () => {
      document.title = previousTitle;
      if (description) {
        if (previousDescription === null || previousDescription === undefined) description.removeAttribute("content");
        else description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setError("This check-in link is missing its session token.");
      setLoading(false);
      return;
    }
    const loadSession = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/checkin/validate?token=${encodeURIComponent(token)}`);
        const payload = await response.json();
        if (!response.ok) throw new Error(errorMessage(payload));
        setSession(payload);
        setDynamicAnswers(Object.fromEntries((payload.dynamic_questions || []).map((question) => [question.key, ""])));
      } catch (loadError) {
        setError(loadError.message || "This check-in link is not available.");
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [token]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateDynamicAnswer = (questionKey, value) => setDynamicAnswers((current) => ({ ...current, [questionKey]: value }));

  const lookupEmail = async () => {
    const email = form.email.trim();
    if (!session || !isValidEmail(email)) return;
    setLookupLoading(true);
    setError("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkin/lookup?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(errorMessage(payload));
      setEmailRegistered(Boolean(payload.registered));
    } catch (lookupError) {
      setEmailRegistered(null);
      setError(lookupError.message || "We could not check this registration email yet.");
    } finally {
      setLookupLoading(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!session || submitting) return;
    if (emailRegistered === false && !form.phone.trim()) {
      setGuestPrompt(true);
      setError("Please add a phone number so we can create your student card.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkin/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          attendee_count: Number(form.attendee_count),
          lesson_start_time: session.session_time,
          dynamic_answers: dynamicQuestions.map((question) => ({ question_key: question.key, answer: String(dynamicAnswers[question.key] || "").trim() })),
          phone: form.phone.trim() || null,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        if (String(payload?.detail || "").toLowerCase().includes("phone")) setGuestPrompt(true);
        throw new Error(errorMessage(payload));
      }
      setSubmitted(payload);
      toast.success("Attendance Verified! Enjoy your lesson.");
    } catch (submitError) {
      setError(submitError.message || "We could not record your check-in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <EmergencyAnnouncementBar />
      <div className="public-checkin-page" data-testid="public-checkin-page">
        <main className="public-checkin-shell">
        <a className="public-checkin-brand" href="/" data-testid="public-checkin-brand-link"><img src="/assets/logo-cropped.png" alt="MyEnglish Lesson.ca" /></a>
        <section className="public-checkin-card" data-testid="public-checkin-card">
          {loading ? <div className="public-checkin-loading" data-testid="public-checkin-loading">Loading your session details…</div> : error && !session ? <div className="public-checkin-error-state"><div className="public-checkin-icon public-checkin-icon-error"><Clock3 size={24} /></div><div><div className="public-checkin-kicker">CHECK-IN LINK UNAVAILABLE</div><h1>This link needs attention</h1><p data-testid="public-checkin-error">{error}</p></div><a className="button button-outline" href="/" data-testid="public-checkin-home-link">Return home</a></div> : submitted ? <div className="public-checkin-success" data-testid="public-checkin-success-banner"><div className="public-checkin-icon"><CheckCircle2 size={27} /></div><div className="public-checkin-kicker">ATTENDANCE VERIFIED</div><h1>Attendance Verified! Enjoy your lesson.</h1><div className="public-checkin-confirmation-time" data-testid="public-checkin-success-timestamp">Recorded {formatLocalDateTime(submitted.recorded_at)}</div><a className="button button-outline" href="/" data-testid="public-checkin-home-link">Return home</a></div> : <>
            <div className="public-checkin-kicker">LIVE GROUP PRACTICE · STUDENT CHECK-IN</div>
            <h1>Welcome to today’s session</h1>
            <p className="public-checkin-lead">Confirm the six quick questions below so your attendance and lesson plan are ready.</p>
            <div className="public-checkin-session-info" data-testid="public-checkin-session-info"><div className="public-checkin-time-copy"><span><Clock3 size={15} /> Toronto time</span><strong>{session?.toronto_label}</strong><small>Your local time: {formatLocalDateTime(session?.session_start)}</small></div><span className="public-checkin-session-badge">90-min group practice</span></div>
            <div className="public-checkin-trust-note"><ShieldCheck size={16} /><span>Secure session link · Active for {session?.valid_minutes || 89} minutes · {session?.session_code}</span></div>
            <form className="public-checkin-form" onSubmit={submit}>
              <label>Full Name<input type="text" value={form.full_name} onChange={(event) => updateForm("full_name", event.target.value)} placeholder="Your full name" autoComplete="name" required data-testid="public-checkin-name-input" /></label>
              <label>Email used for lesson registration<input type="email" value={form.email} onChange={(event) => { updateForm("email", event.target.value); setEmailRegistered(null); }} onBlur={lookupEmail} placeholder="student@example.com" autoComplete="email" required data-testid="public-checkin-email-input" />{lookupLoading && <small className="public-checkin-field-hint">Checking your registration…</small>}</label>
              <label>How many students are attending with you today?<select value={form.attendee_count} onChange={(event) => updateForm("attendee_count", event.target.value)} required data-testid="public-checkin-attendee-count-select">{Array.from({ length: 6 }, (_, index) => index + 1).map((count) => <option value={String(count)} key={count}>{count} {count === 1 ? "student" : "students"}</option>)}</select></label>
              <label>Lesson Start Time <span>(Toronto)</span><input type="text" value={session?.toronto_label || ""} readOnly required data-testid="public-checkin-lesson-start-input" /></label>
              {dynamicQuestions.map((question) => <label key={question.key} data-testid={`public-checkin-question-${question.key}`}><span>{question.prompt}</span>{question.input_type === "select" ? <select value={dynamicAnswers[question.key] || ""} onChange={(event) => updateDynamicAnswer(question.key, event.target.value)} required data-testid={`public-checkin-dynamic-${question.key}-input`}><option value="">Select one</option>{question.options.map((option) => <option value={option} key={option}>{option}</option>)}</select> : question.input_type === "textarea" ? <textarea value={dynamicAnswers[question.key] || ""} onChange={(event) => updateDynamicAnswer(question.key, event.target.value)} placeholder="Your answer" required data-testid={`public-checkin-dynamic-${question.key}-input`} /> : <input type="text" value={dynamicAnswers[question.key] || ""} onChange={(event) => updateDynamicAnswer(question.key, event.target.value)} placeholder="Your answer" required data-testid={`public-checkin-dynamic-${question.key}-input`} />}</label>)}
              {emailRegistered === false || guestPrompt ? <label className="public-checkin-guest-field">Phone number <span>(new student card)</span><input type="tel" value={form.phone} onChange={(event) => updateForm("phone", event.target.value)} placeholder="416-555-0123" autoComplete="tel" required data-testid="public-checkin-phone-input" /></label> : null}
              {error && <div className="public-checkin-form-error" role="alert" data-testid="public-checkin-form-error">{error}</div>}
              <button className="button button-crimson public-checkin-submit" type="submit" disabled={submitting} data-testid="public-checkin-submit-button"><CheckCircle2 size={17} /> {submitting ? "Recording…" : "Confirm My Check-In"}</button>
            </form>
          </>}
        </section>
        <p className="public-checkin-help">Questions or need to reschedule? <a href="mailto:learn@myenglishlesson.ca" data-testid="public-checkin-help-link">Email learn@myenglishlesson.ca</a></p>
      </main>
      <Toaster position="bottom-right" richColors closeButton />
      </div>
    </>
  );
};

export default CheckinPage;
